<template lang="pug">
  .color-kit-pdp-v2(itemprop="product" itemscope itemtype="http://schema.org/Product")
    PdpBanner

    template(v-if="hasActiveSub")
      PdpExistingSubBanner(:quiz-completed="advisorCompleted" quiz-url="/color-advisor?rectype=color" more-colors-url="/shop/products")

    .not-eligible-message.container.brand-color-2-bg.ui-color-1(v-if="showNotEligibleMessage")
      .row
        .xs-12
          .inner-wrap.fixed-width-tweak-upper
            mr-icon.ui-color-1(name="info" height="3em" :fill="true")
            p.ml-50m.xs-f-small
              span.f-primary.xs-f-small.max-at-tweak Based on your&nbsp;
              a.f-primary.underline.color-white.xs-f-small.max-at-tweak(href="/color-advisor?rectype=color") quiz
              span.f-primary.xs-f-small.max-at-tweak &nbsp;answers, we recommend&nbsp;
              a.f-primary.underline.color-white.xs-f-small.max-at-tweak(:href="colorWonderBestMatchUrl") Demi-Permanent Hair Color
              span &nbsp;for you.

    ColorKitHeroV2

    //- UGC images
    .row.mb-150m.pdp-ugc-wrap
      DashHudsonScript(location="RCC PDP" :version="2")
        template(#title="")
          h3.kapra-neue.brand-color-1.upper.xs-f-xmedium.lg-f-xgrande.max-at-tweak Real clients, really gorgeous results

    LazyComponent.branded-banner-lazy-wrap(v-if="cmsSettings.showBrandedBannerOne && cmsSettings.brandedBannerOne && cmsSettings.brandedBannerOne.cms_partial")
      template(#default="{ shown }")
        .row(v-if="shown")
          CMSPartial(:mixin-key="cmsSettings.brandedBannerOne.cms_partial")

    LazyComponent.color-crew-module-lazy-wrap(v-if="cmsSettings.colorCrewModule && cmsSettings.colorCrewModule.cms_partial")
      template(#default="{ shown }")
        cms-partial(v-if="shown" :mixin-key="cmsSettings.colorCrewModule.cms_partial")

    LazyComponent.pdp-visit-us-lazy-wrap
      template(#default="{ shown }")
        ColorBarVisitUsSection(v-if="shown")

    //- Xsell
    LazyComponent.pdp-xsell-lazy-wrap
      template(#default="{ shown }")
        PDPXsell(v-if="shown && product.code")

    //- Recently viewed
    LazyComponent.pdp-recently-viewed-lazy-wrap
      template(#default="{ shown }")
        PDPRecentlyViewed(v-if="shown && product.url_key" :product-url-key="product.url_key")

    //- HairspirationalCarousel
    LazyComponent.pdp-hairspiration-lazy-wrap
      template(#default="{ shown }")
        HairspirationalCarousel(v-if="shown")

    .row(v-if="questionsAndAnswers.length > 0").xs-pl-150m.xs-pr-150m.xs-pb-200m.xl-pr-0m.xl-pl-0m.xs-mt-200m.md-mt-300m
      Tabs(:tabs="tabGroup" @tab-set="trackTabs" theme="tertiary" min-tab-height="15em" )
        template(#reviews="")
          Reviews(hide-title)

        template(#faqs="")
          .row.xs-mt-100m
            Faqs(:faqs="questionsAndAnswers")

    //- Reviews
    Reviews(v-else)

    LazyComponent.pdp-no-dead-end-ctas-lazy-wrap
      template(#default="{ shown }")
        NoDeadEndCtas(v-if="shown")

</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex';
  import vueAdvisorSvc from '@services/vueAdvisorSvc';

  import ColorKitHeroV2 from '@components/PDP/ColorKitPdpV2/ColorKitHeroV2';
  import LazyComponent from '@components/LazyComponent';
  import PdpBanner from '@components/PDP/PdpBanner';
  import Reviews from '@components/PDP/Reviews';
  import PdpExistingSubBanner from '@components/PDP/PdpExistingSubBannerV2';
  import trackEventMixin from '@mixins/trackEventMixin';
  import DashHudsonScript from "@components/PDP/DashHudsonScript.vue";
  import Tabs from "@components/Tabs";
  import CMSPartial from "@components/CMSPartial";

  export default {
    name: 'ColorKitPdpV2',

    components: {
      Tabs,
      CMSPartial,
      DashHudsonScript,
      PdpExistingSubBanner,
      LazyComponent,
      PdpBanner,
      ColorKitHeroV2,
      Reviews,
      Faqs: () => import('@components/Faqs/'),
      GuaranteeContactUs: () => import('@components/PDP/GuaranteeContactUs'),
      PDPXsell: () => import('@components/PDP/PDPXsell'),
      HairspirationalCarousel: () => import('@components/PDP/HairspirationalCarousel'),
      ColorBarVisitUsSection: () => import('@components/PDP/ColorBarVisitUsSection'),
      PDPRecentlyViewed: () => import('@components/PDP/PDPRecentlyViewed'),
      NoDeadEndCtas: () => import('@components/PDP/ColorKitPdpV2/NoDeadEndCtas'),
    },

    mixins: [trackEventMixin],
    inject: ['getObjProperty'],

    data() {
      return {
        howToUseSectionExpanded: false,
        utmModalTimerUp: false,
        eventsTracked: false,
        tabGroup: [
          { title: "Reviews", code: 'reviews' },
          { title: "FAQs", code: 'faqs' },
        ]
      };
    },

    computed: {
      ...mapState('pdp', ['version', 'product', 'addPerfectPair', 'sawPerfectPair', 'subscription']),
      ...mapGetters('pdp', ['hasActiveSub', 'showPerfectPairOnPdp', 'perfectPairEligible', 'perfectPairGloss', 'pdpDataLoaded']),
      ...mapState('cms', ['cmsSettings']),
      ...mapState('xsell', ['selectedBoost', 'productXsellItems']),
      ...mapState('cart', ['cart']),
      ...mapGetters('xsell', ['xsellGloss']),
      ...mapState('customer', ['cdata', 'refreshingCdata']),
      ...mapGetters('customer', ['visitorType']),
      ...mapState('colorAdvisor', ['answers', 'advisorCompleted']),

      bestMatch() {
        return this.cdata.bestColorMatch;
      },

      showNotEligibleMessage() {
        return this.advisorCompleted && this.bestMatch?.productType === "colorwonder" && this.version === 'B' && this.experiments['NC RCC PDP Swatches Feb 2025'] === 'B';
      },

      colorWonderBestMatchUrl() {
        return this.bestMatch?.productType === "colorwonder" ? `/products/${this.bestMatch?.urlKey}` : '/color-advisor';
      },

      questionsAndAnswers() {
        let productTypeQuestions = this.getObjProperty(this, 'product.productTypeFaqs') || [];
        let productQuestions = this.getObjProperty(this, 'product.productFaqs') || [];
        return productTypeQuestions.concat(productQuestions);
      },

      inUTMModalTest() {
        if (this.pdpDataLoaded && !this.refreshingCdata && !this.advisorCompleted && this.visitorType != 'return buyer' && this.$route.query.utm_campaign && this.$cookies.get('utm-advisor-modal') != 'viewed') {
          return true;
        } else {
          return false;
        }
      },
    },

    watch: {
      product() {
        if (this.$route.query.cr) {
          this.updateColoristRecommendedProduct();
        }
      },

      inUTMModalTest(value) {
        if (value && !this.eventsTracked) {
          this.trackEvents();
        }
      }
    },

    created() {
      // open try it on modal via urlParam
      if (this.$route.query.try) {
        this.openPlaygroundModal();
      }

      if (this.$route.query.purchasePanel || this.$route.hash == '#purchase-modal') {
        let modalPayload = {
          theme: 'full-screen',
        };

        this.openPurchasePanelModal(modalPayload);
      }

      // colorist can direct user to a recommended color by passing "cr=true" from params
      if (this.$route.query.cr) {
        this.updateColoristRecommendedProduct();
      }

      this.initColorAdvisor();
      this.isAdvisorCompleted();
    },

    mounted() {
      if (this.inUTMModalTest) {
        this.trackEvents();
      }
    },

    methods: {
      ...mapActions('pdp', ['openPlaygroundModal', 'openPurchasePanelModal']),
      ...mapActions('colorAdvisor', ['initColorAdvisor', 'isAdvisorCompleted']),
      ...mapActions('modal', ['hideModal']),

      ...mapActions('partial', [
        'showPartialModal',
      ]),

      trackAccordion(data) {
        this.trackMREvent(`${data.title} Section Toggled`, {
          product_name: this.product.name,
          product_code: this.product.code,
          product_id: this.product.id,
        });
      },

      trackTabs(section) {
        this.trackMREvent(`RCC Color Kit PDP - ${section.title} tab Clicked`);
      },

      updateColoristRecommendedProduct() {
        if (this.product.code) {
          vueAdvisorSvc
            .persistCustomerAnswers({
              quizName: 'colorist-recommended',
              answers: {
                colorist_match: this.product.code,
                last_match_type: 'colorist',
              },
            })
            .then(() => {
              this.$root.advisorUpdated += 1;
            });
        }
      },

      trackEvents() {
        this.eventsTracked = true;
        setTimeout(() => {
          this.mix_trackExperimentViewed('RCC PDP FTV URM Advisor Modal Apr 2022');
          if (this.experiments['RCC PDP FTV URM Advisor Modal Apr 2022'] == 'B') {
            this.$cookies.set('utm-advisor-modal', 'viewed', '1d');
            this.trackMREvent('PDP - Viewed CA Popup');
            this.showPartialModal({
              mixinKey: 'partial-rcc-pdp-ftv-utm-advisor-modal',
              theme: 'bottom-mobile-slide-no-padding'
            });
          }
        }, 3000);
      }
    },
  };
</script>

<style scoped lang="stylus">
  .color-kit-pdp-v2
    .not-eligible-message.container
      .inner-wrap
        display flex
        align-items center
        padding 1em

    .non-pp-msg
      .msg
        background-color ui-color-4
        position relative

        i
          position absolute

        p
          padding-left 2em

    .complete-range
      border-top 1px solid color-fog

      .range
        padding 1.5em 5%

    .expertise-included
      padding-top 3em
      border-top 1px solid #cbc8c7

    .review-wrap
      .reviews
        border-bottom 1px solid #cbc8c7
        padding-bottom 2em
        margin-bottom 3em

      .title-wrap
        padding 1.5em 0

        .section-title
          padding-bottom 0.6em

        .desc-short
          padding-bottom 0.5em


    @media mq-tablet-plus
      .not-eligible-message
        .inner-wrap
          padding-left 3%
          padding-right 3%

      .all-colors
        padding 0 2%

    @media mq-mobile
      .guarantee-visit-us-container
        flex-direction column-reverse

</style>